import { useCallback, useRef, useState } from "react";
import { fetchData } from "../api/client";

const POLL_MS = 3000;

export const HYGIENE_RULES = [
  { id: "phone_normalize", label: "Normalizar telefones (9º dígito / E.164)" },
  { id: "phone_invalid", label: "Telefones inválidos" },
  { id: "email_invalid", label: "E-mails inválidos ou mal formatados" },
  { id: "email_missing", label: "E-mails ausentes" },
  { id: "cpf_invalid", label: "CPF inválido" },
  { id: "cnpj_invalid", label: "CNPJ inválido" },
  { id: "name_invalid", label: "Nomes suspeitos ou inválidos" },
  { id: "name_missing", label: "Nomes ausentes" },
  { id: "address_missing", label: "Endereço ausente" },
  { id: "duplicate_candidate", label: "Possíveis duplicatas omnichannel (unificação manual)" },
  { id: "phone_cross_duplicate", label: "Telefone redundante (igual ao celular)" },
];

function hygieneJobError(e, fallback) {
  const err = new Error(e?.message || fallback);
  err.status = e?.status;
  err.code = e?.code || e?.data?.code;
  err.activeJobId = e?.data?.activeJobId || e?.activeJobId;
  err.activeJob = e?.data?.job || e?.activeJob;
  return err;
}

export function useClientHygiene() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const scanSync = useCallback(async ({ ids, filters, rules }) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchData("/clients/hygiene/scan", "POST", {
        ids,
        filters,
        rules,
      });
      const data = resp?.data ?? resp;
      return data;
    } catch (e) {
      setError(e?.message || "Erro na varredura.");
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const startJob = useCallback(async ({ filters, ids, rules, mode }) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchData("/clients/hygiene/jobs", "POST", {
        filters,
        ids,
        rules,
        mode,
      });
      const data = resp?.data ?? resp;
      return data;
    } catch (e) {
      const wrapped = hygieneJobError(e, "Erro ao enfileirar varredura.");
      setError(wrapped.message);
      throw wrapped;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchJob = useCallback(async (jobId, { light = false } = {}) => {
    const suffix = light ? "?light=1" : "";
    const resp = await fetchData(`/clients/hygiene/jobs/${jobId}${suffix}`, "GET");
    return resp?.data ?? resp;
  }, []);

  const pollJob = useCallback(
    (jobId, { onUpdate, onComplete, onError } = {}) => {
      stopPolling();
      const tick = async () => {
        try {
          const job = await fetchJob(jobId, { light: true });
          onUpdate?.(job);
          if (["completed", "failed", "cancelled"].includes(job?.status)) {
            stopPolling();
            if (job.status === "completed") {
              const full = await fetchJob(jobId, { light: false });
              onComplete?.(full);
            } else {
              onError?.(job);
            }
          }
        } catch (e) {
          stopPolling();
          onError?.(e);
        }
      };
      tick();
      pollRef.current = setInterval(tick, POLL_MS);
    },
    [stopPolling, fetchJob]
  );

  const cancelJob = useCallback(async (jobId) => {
    await fetchData(`/clients/hygiene/jobs/${jobId}/cancel`, "POST", {});
  }, []);

  const applyFixes = useCallback(async ({ scanToken, issueIds, applyAllAutoFixable }) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchData("/clients/hygiene/apply", "POST", {
        scanToken,
        issueIds,
        applyAllAutoFixable,
      });
      return resp?.data ?? resp;
    } catch (e) {
      setError(e?.message || "Erro ao aplicar correções.");
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    scanSync,
    startJob,
    pollJob,
    cancelJob,
    fetchJob,
    applyFixes,
    stopPolling,
    clearError,
  };
}
